import React,{useEffect,useMemo,useState}from"react";
import{Autocomplete,Box,Button,Dialog,DialogActions,DialogContent,DialogTitle,FormControl,InputLabel,MenuItem,Paper,Select,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,TextField,Typography}from"@mui/material";
import{useNavigate,useParams}from"react-router-dom";
import Sidebar from"../AdminPanel/Sidebar";
import TopBar from"../AdminPanel/TopBar";
import{useDashboardTheme}from"../../../context/DashboardThemeContext";
import{useAuth}from"../../../context/AuthContext";
import{useAlert}from"../../../context/AlertContext";

const API_BASE="/api/purchases";
const today=()=>new Date().toISOString().slice(0,10);
const emptyForm=()=>({origen:"MANUAL",tipo_documento:"SIN_COMPROBANTE",numero_documento:"",clave_acceso:"",autorizacion:"",tercero_id:"",tercero_identificacion:"",tercero_razon_social:"",fecha_documento:today(),concepto:"",observacion:""});
const emptyDetail=()=>({destino:"GASTO",producto_id:"",categoria_id:"",codigo:"",descripcion:"",cantidad:"1",precio_unitario:"0",descuento:"0",iva_porcentaje:"0"});
const emptyPayment=()=>({fecha_pago:today(),caja:"",efectivo:"",banco:"",codigo_transferencia:"",observacion:""});

function getToken(){
  return localStorage.getItem("accessToken")||localStorage.getItem("access")||"";
}
function getCookie(name){
  const cookies=document.cookie?document.cookie.split(";"):[];
  for(const cookie of cookies){
    const parts=cookie.trim().split("=");
    if(parts[0]===name)return decodeURIComponent(parts.slice(1).join("="));
  }
  return"";
}
async function ensureCsrfToken(){
  let token=getCookie("csrftoken");
  if(token)return token;
  try{
    const response=await fetch("/sistema/get-csrf-token/",{method:"GET",credentials:"include",headers:{Accept:"application/json"}});
    const data=await response.json().catch(()=>({}));
    token=getCookie("csrftoken")||data?.csrfToken||data?.csrf_token||"";
  }catch{
    token="";
  }
  return token;
}
function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return(user.permissions||[]).includes(code);
}
function normalizeDecimalInput(value,maxDecimals=6){
  let text=String(value??"").replace(/,/g,".");
  text=text.replace(/[^\d.]/g,"");
  const firstDot=text.indexOf(".");
  if(firstDot!==-1)text=text.slice(0,firstDot+1)+text.slice(firstDot+1).replace(/\./g,"");
  if(text.startsWith("."))text=`0${text}`;
  if(!text)return"";
  const parts=text.split(".");
  let integer=parts[0];
  integer=integer.replace(/^0+(?=\d)/,"");
  if(parts.length>1)return`${integer||"0"}.${parts[1].slice(0,maxDecimals)}`;
  return integer;
}
function decimalNumber(value){
  const number=Number(String(value??"").replace(",","."));
  return Number.isFinite(number)?number:0;
}
function moneyCents(value){
  return Math.round((decimalNumber(value)+Number.EPSILON)*100);
}
function formatMoney(value){
  return(moneyCents(value)/100).toFixed(2);
}
function formatDate(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  if(parts.length===3)return`${parts[2]}/${parts[1]}/${parts[0]}`;
  return value;
}
function paymentStatus(value){
  if(value==="PAGADO")return"Pagado";
  if(value==="PARCIAL")return"Parcial";
  return"Pendiente";
}
function calculateDetail(item){
  const cantidad=decimalNumber(item.cantidad);
  const precio=decimalNumber(item.precio_unitario);
  const descuento=decimalNumber(item.descuento);
  const iva=decimalNumber(item.iva_porcentaje);
  const subtotal=Math.max(0,(cantidad*precio)-descuento);
  const ivaValor=subtotal*(iva/100);
  return{subtotal,ivaValor,total:subtotal+ivaValor};
}

export default function PurchaseForm(){
  const[sidebarOpen,setSidebarOpen]=useState(true);
  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();
  const{showError,showSuccess,showInfo}=useAlert();
  const navigate=useNavigate();
  const{purchaseId}=useParams();
  const token=getToken();
  const isNew=!purchaseId;
  const[empresaId,setEmpresaId]=useState("");
  const[empresaLoading,setEmpresaLoading]=useState(true);
  const[loading,setLoading]=useState(false);
  const[saving,setSaving]=useState(false);
  const[readingXml,setReadingXml]=useState(false);
  const[actionLoading,setActionLoading]=useState(false);
  const[paymentSaving,setPaymentSaving]=useState(false);
  const[paymentAnnulling,setPaymentAnnulling]=useState(false);
  const[form,setForm]=useState(emptyForm());
  const[details,setDetails]=useState([emptyDetail()]);
  const[catalogs,setCatalogs]=useState({categorias:[],terceros:[],productos:[],tipos_documento:[]});
  const[purchase,setPurchase]=useState(null);
  const[xmlFile,setXmlFile]=useState(null);
  const[initialPayment,setInitialPayment]=useState(emptyPayment());
  const[additionalPayment,setAdditionalPayment]=useState(emptyPayment());
  const[annulPurchaseOpen,setAnnulPurchaseOpen]=useState(false);
  const[motivoAnulacion,setMotivoAnulacion]=useState("");
  const[paymentToAnnul,setPaymentToAnnul]=useState(null);
  const[motivoPagoAnulacion,setMotivoPagoAnulacion]=useState("");
  const canCreate=userCan(user,"compras.create");
  const canDelete=userCan(user,"compras.delete");
  const editable=isNew&&canCreate;
  const documentState=purchase?.estado||"REGISTRADO";

  const parseError=data=>{
    const error=data?.error||data?.detail||data?.mensaje;
    if(!error)return"No se pudo procesar la solicitud.";
    if(typeof error==="string")return error;
    if(Array.isArray(error))return error.join(" ");
    if(typeof error==="object")return Object.values(error).flat().join(" ");
    return"No se pudo procesar la solicitud.";
  };

  const apiFetch=async(url,options={})=>{
    const method=String(options.method||"GET").toUpperCase();
    const headers={Accept:"application/json",...(options.headers||{})};
    if(token)headers.Authorization=`Bearer ${token}`;
    if(!["GET","HEAD","OPTIONS"].includes(method)){
      const csrfToken=await ensureCsrfToken();
      if(csrfToken)headers["X-CSRFToken"]=csrfToken;
    }
    return fetch(url,{...options,method,headers,credentials:"include"});
  };

  const responseData=async response=>{
    try{return await response.json();}catch{return{};}
  };

  const loadEmpresa=async()=>{
    try{
      setEmpresaLoading(true);
      const response=await apiFetch("/api/enterprise/empresa/obtener/");
      const data=await responseData(response);
      if(!response.ok){showError(parseError(data)||"No se pudo cargar la empresa.");return;}
      if(!data?.empresa?.id){showError("No existe una empresa configurada.");return;}
      setEmpresaId(data.empresa.id);
    }catch{
      showError("Error de conexión al cargar la empresa.");
    }finally{
      setEmpresaLoading(false);
    }
  };

  const loadCatalogs=async id=>{
    try{
      const response=await apiFetch(`${API_BASE}/catalogos/?empresa_id=${id}`);
      const data=await responseData(response);
      if(!response.ok){showError(parseError(data));return;}
      setCatalogs({categorias:data.categorias||[],terceros:data.terceros||[],productos:data.productos||[],tipos_documento:(data.tipos_documento||[]).filter(item=>item.value!=="ROL_PAGO")});
    }catch{
      showError("Error de conexión al cargar los catálogos.");
    }
  };

  const loadPurchase=async id=>{
    if(!purchaseId)return;
    try{
      setLoading(true);
      const response=await apiFetch(`${API_BASE}/${purchaseId}/?empresa_id=${id}`);
      const data=await responseData(response);
      if(!response.ok){showError(parseError(data));return;}
      const item=data.compra||{};
      setPurchase(item);
      setForm({origen:item.origen||"MANUAL",tipo_documento:item.tipo_documento||"SIN_COMPROBANTE",numero_documento:item.numero_documento||"",clave_acceso:item.clave_acceso||"",autorizacion:item.autorizacion||"",tercero_id:item.tercero_id||"",tercero_identificacion:item.tercero_identificacion||"",tercero_razon_social:item.tercero_razon_social||"",fecha_documento:item.fecha_documento||today(),concepto:item.concepto||"",observacion:item.observacion||""});
      setDetails((item.detalles||[]).length?item.detalles.map(detail=>({destino:detail.destino||"GASTO",producto_id:detail.producto_id||"",categoria_id:detail.categoria_id||"",codigo:detail.codigo||"",descripcion:detail.descripcion||"",cantidad:String(detail.cantidad??"1"),precio_unitario:String(detail.precio_unitario??"0"),descuento:String(detail.descuento??"0"),iva_porcentaje:String(detail.iva_porcentaje??"0")})):[emptyDetail()]);
    }catch{
      showError("Error de conexión al cargar el documento.");
    }finally{
      setLoading(false);
    }
  };

  useEffect(()=>{loadEmpresa();},[]);
  useEffect(()=>{
    if(!empresaId)return;
    loadCatalogs(empresaId);
    if(isNew){
      setPurchase(null);
      setForm(emptyForm());
      setDetails([emptyDetail()]);
      setXmlFile(null);
      setInitialPayment(emptyPayment());
    }else loadPurchase(empresaId);
  },[empresaId,purchaseId]);

  const handleForm=(field,value)=>setForm(prev=>({...prev,[field]:value}));
  const selectedThirdParty=useMemo(()=>catalogs.terceros.find(item=>String(item.id)===String(form.tercero_id))||null,[catalogs.terceros,form.tercero_id]);

  const handleThirdParty=value=>{
    if(!value){setForm(prev=>({...prev,tercero_id:"",tercero_identificacion:"",tercero_razon_social:""}));return;}
    setForm(prev=>({...prev,tercero_id:value.id,tercero_identificacion:value.ruc||value.cedula||"",tercero_razon_social:value.razon_social||value.nombre_comercial||""}));
  };

  const updateDetail=(index,field,value)=>{
    setDetails(prev=>prev.map((item,itemIndex)=>{
      if(itemIndex!==index)return item;
      const numericFields={cantidad:6,precio_unitario:6,descuento:6,iva_porcentaje:2};
      const next={...item,[field]:Object.prototype.hasOwnProperty.call(numericFields,field)?normalizeDecimalInput(value,numericFields[field]):value};
      if(field==="destino"){
        if(value==="GASTO")next.producto_id="";
        if(value==="INVENTARIO")next.categoria_id="";
      }
      if(field==="producto_id"){
        const product=catalogs.productos.find(productItem=>String(productItem.id)===String(value));
        if(product){
          next.codigo=product.code||product.codigo_auxiliar||product.barcode||next.codigo;
          next.descripcion=product.name||next.descripcion;
        }
      }
      return next;
    }));
  };

  const addDetail=()=>setDetails(prev=>[...prev,emptyDetail()]);
  const removeDetail=index=>{
    if(details.length<=1){showInfo("Debe existir al menos un detalle.");return;}
    setDetails(prev=>prev.filter((_item,itemIndex)=>itemIndex!==index));
  };

  const totalDocument=details.reduce((total,item)=>total+calculateDetail(item).total,0);
  const initialCaja=decimalNumber(initialPayment.caja);
  const initialEfectivo=decimalNumber(initialPayment.efectivo);
  const initialBanco=decimalNumber(initialPayment.banco);
  const initialPaymentTotal=initialCaja+initialEfectivo+initialBanco;
  const additionalCaja=decimalNumber(additionalPayment.caja);
  const additionalEfectivo=decimalNumber(additionalPayment.efectivo);
  const additionalBanco=decimalNumber(additionalPayment.banco);
  const additionalPaymentTotal=additionalCaja+additionalEfectivo+additionalBanco;
  const purchaseTotal=isNew?totalDocument:decimalNumber(purchase?.total);
  const totalPaid=decimalNumber(purchase?.total_pagado);
  const pendingBalance=decimalNumber(purchase?.saldo_pendiente);

  const updateInitialPayment=(field,value)=>{
    setInitialPayment(prev=>({...prev,[field]:["caja","efectivo","banco"].includes(field)?normalizeDecimalInput(value,2):value}));
  };

  const updateAdditionalPayment=(field,value)=>{
    setAdditionalPayment(prev=>({...prev,[field]:["caja","efectivo","banco"].includes(field)?normalizeDecimalInput(value,2):value}));
  };

  const setInitialFullPayment=medio=>{
    const total=formatMoney(totalDocument);
    setInitialPayment(prev=>({...prev,caja:medio==="CAJA"?total:"0.00",efectivo:medio==="EFECTIVO"?total:"0.00",banco:medio==="BANCO"?total:"0.00",codigo_transferencia:medio==="BANCO"?prev.codigo_transferencia:""}));
  };

  const setAdditionalFullPayment=medio=>{
    const total=formatMoney(purchase?.saldo_pendiente);
    setAdditionalPayment(prev=>({...prev,caja:medio==="CAJA"?total:"0.00",efectivo:medio==="EFECTIVO"?total:"0.00",banco:medio==="BANCO"?total:"0.00",codigo_transferencia:medio==="BANCO"?prev.codigo_transferencia:""}));
  };

  const readXml=async file=>{
    if(!file||!empresaId)return;
    try{
      setReadingXml(true);
      const body=new FormData();
      body.append("empresa_id",empresaId);
      body.append("xml",file);
      const response=await apiFetch(`${API_BASE}/xml/leer/`,{method:"POST",body});
      const data=await responseData(response);
      if(!response.ok){showError(parseError(data));return;}
      const doc=data.documento||{};
      if(doc.ya_registrada){showInfo("Este XML ya se encuentra registrado.");return;}
      setXmlFile(file);
      setForm(prev=>({...prev,origen:"XML",tipo_documento:"FACTURA",numero_documento:doc.numero_documento||"",clave_acceso:doc.clave_acceso||"",autorizacion:doc.autorizacion||doc.clave_acceso||"",tercero_id:doc.tercero_id||"",tercero_identificacion:doc.tercero_identificacion||"",tercero_razon_social:doc.tercero_razon_social||"",fecha_documento:doc.fecha_documento||prev.fecha_documento,concepto:doc.tercero_razon_social?`Compra a ${doc.tercero_razon_social}`:prev.concepto}));
      const xmlDetails=(doc.detalles||[]).map(item=>({destino:item.destino||"GASTO",producto_id:item.producto_id||"",categoria_id:item.categoria_id||"",codigo:item.codigo||item.codigo_auxiliar||"",descripcion:item.descripcion||"",cantidad:normalizeDecimalInput(item.cantidad??"1",6),precio_unitario:normalizeDecimalInput(item.precio_unitario??"0",6),descuento:normalizeDecimalInput(item.descuento??"0",6),iva_porcentaje:normalizeDecimalInput(item.iva_porcentaje??"0",2)}));
      setDetails(xmlDetails.length?xmlDetails:[emptyDetail()]);
      showSuccess("XML leído correctamente.");
    }catch{
      showError("Error de conexión al procesar el XML.");
    }finally{
      setReadingXml(false);
    }
  };

  const validatePurchase=()=>{
    if(!form.fecha_documento){showInfo("Debe seleccionar la fecha.");return false;}
    if(!details.length){showInfo("Debe ingresar al menos un detalle.");return false;}
    for(const item of details){
      if(!item.descripcion.trim()){showInfo("Todos los detalles deben tener una descripción.");return false;}
      if(decimalNumber(item.cantidad)<=0){showInfo("Todas las cantidades deben ser mayores a cero.");return false;}
      if(decimalNumber(item.precio_unitario)<0){showInfo("El precio unitario no puede ser negativo.");return false;}
      if(decimalNumber(item.descuento)<0){showInfo("El descuento no puede ser negativo.");return false;}
      if(decimalNumber(item.iva_porcentaje)<0){showInfo("El IVA no puede ser negativo.");return false;}
      if(item.destino==="INVENTARIO"&&!item.producto_id){showInfo(`Seleccione el producto para "${item.descripcion}".`);return false;}
      if(item.destino==="GASTO"&&!item.categoria_id){showInfo(`Seleccione la categoría para "${item.descripcion}".`);return false;}
    }
    if(initialPaymentTotal<0){showInfo("El pago no puede ser negativo.");return false;}
    if(initialBanco>0&&!initialPayment.codigo_transferencia.trim()){showInfo("Debe ingresar el código de la transferencia bancaria.");return false;}
    if(moneyCents(initialPaymentTotal)>moneyCents(totalDocument)){showInfo("El pago inicial no puede superar el total de la compra.");return false;}
    return true;
  };

  const savePurchase=async()=>{
    if(!editable||saving||!validatePurchase())return;
    try{
      setSaving(true);
      const body=new FormData();
      body.append("empresa_id",empresaId);
      body.append("origen",form.origen);
      body.append("tipo_documento",form.tipo_documento);
      body.append("numero_documento",form.numero_documento);
      body.append("clave_acceso",form.clave_acceso);
      body.append("autorizacion",form.autorizacion);
      body.append("tercero_id",form.tercero_id);
      body.append("tercero_identificacion",form.tercero_identificacion);
      body.append("tercero_razon_social",form.tercero_razon_social);
      body.append("fecha_documento",form.fecha_documento);
      body.append("concepto",form.concepto);
      body.append("observacion",form.observacion);
      body.append("detalles",JSON.stringify(details.map(item=>({...item,cantidad:item.cantidad||"0",precio_unitario:item.precio_unitario||"0",descuento:item.descuento||"0",iva_porcentaje:item.iva_porcentaje||"0"}))));
      body.append("pago",JSON.stringify({fecha_pago:initialPayment.fecha_pago,caja:initialPayment.caja||"0",efectivo:initialPayment.efectivo||"0",banco:initialPayment.banco||"0",codigo_transferencia:initialPayment.codigo_transferencia,observacion:initialPayment.observacion}));
      if(xmlFile)body.append("xml_original",xmlFile);
      const response=await apiFetch(`${API_BASE}/crear/`,{method:"POST",body});
      const data=await responseData(response);
      if(!response.ok){showError(parseError(data));return;}
      showSuccess(data.mensaje||"Compra o gasto registrado correctamente.");
      if(data?.compra?.id)navigate(`/admin-panel/compras/${data.compra.id}`,{replace:true});
      else navigate("/admin-panel/compras",{replace:true});
    }catch{
      showError("Error de conexión.");
    }finally{
      setSaving(false);
    }
  };

  const saveAdditionalPayment=async()=>{
    if(!purchaseId||!empresaId||paymentSaving)return;
    if(additionalPaymentTotal<=0){showInfo("Debe ingresar un valor en caja, efectivo o banco.");return;}
    if(additionalBanco>0&&!additionalPayment.codigo_transferencia.trim()){showInfo("Debe ingresar el código de la transferencia bancaria.");return;}
    if(moneyCents(additionalPaymentTotal)>moneyCents(pendingBalance)){showInfo("El pago no puede superar el saldo pendiente.");return;}
    try{
      setPaymentSaving(true);
      const response=await apiFetch(`${API_BASE}/${purchaseId}/pagos/crear/`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({empresa_id:empresaId,fecha_pago:additionalPayment.fecha_pago,caja:additionalPayment.caja||"0",efectivo:additionalPayment.efectivo||"0",banco:additionalPayment.banco||"0",codigo_transferencia:additionalPayment.codigo_transferencia,observacion:additionalPayment.observacion})});
      const data=await responseData(response);
      if(!response.ok){showError(parseError(data));return;}
      showSuccess(data.mensaje||"Pago registrado correctamente.");
      setAdditionalPayment(emptyPayment());
      await loadPurchase(empresaId);
    }catch{
      showError("Error de conexión.");
    }finally{
      setPaymentSaving(false);
    }
  };

  const annulPurchase=async()=>{
    const motivo=motivoAnulacion.trim();
    if(!motivo){showInfo("Debe indicar el motivo de la anulación.");return;}
    try{
      setActionLoading(true);
      const response=await apiFetch(`${API_BASE}/${purchaseId}/anular/`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({empresa_id:empresaId,motivo})});
      const data=await responseData(response);
      if(!response.ok){showError(parseError(data));return;}
      setAnnulPurchaseOpen(false);
      setMotivoAnulacion("");
      showSuccess(data.mensaje||"Documento anulado correctamente.");
      await loadPurchase(empresaId);
    }catch{
      showError("Error de conexión.");
    }finally{
      setActionLoading(false);
    }
  };

  const annulPayment=async()=>{
    if(!paymentToAnnul)return;
    const motivo=motivoPagoAnulacion.trim();
    if(!motivo){showInfo("Debe indicar el motivo de la anulación.");return;}
    try{
      setPaymentAnnulling(true);
      const response=await apiFetch(`${API_BASE}/pagos/${paymentToAnnul.id}/anular/`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({empresa_id:empresaId,motivo})});
      const data=await responseData(response);
      if(!response.ok){showError(parseError(data));return;}
      setPaymentToAnnul(null);
      setMotivoPagoAnulacion("");
      showSuccess(data.mensaje||"Pago anulado correctamente.");
      await loadPurchase(empresaId);
    }catch{
      showError("Error de conexión.");
    }finally{
      setPaymentAnnulling(false);
    }
  };

  const outlinedButtonSx={minHeight:40,px:2.4,borderRadius:"12px",textTransform:"none",fontWeight:700,color:dashboardTheme.button_color,borderColor:dashboardTheme.button_color,boxShadow:"none","&:hover":{borderColor:dashboardTheme.button_color,bgcolor:`${dashboardTheme.button_color}12`,boxShadow:"none"}};
  const dangerButtonSx={minHeight:40,px:2.4,borderRadius:"12px",textTransform:"none",fontWeight:700,color:"#fff",bgcolor:"#D32F2F",borderColor:"#D32F2F",boxShadow:"none","&:hover":{color:"#fff",bgcolor:"#B71C1C",borderColor:"#B71C1C",boxShadow:"none"},"&.Mui-disabled":{color:"#fff",bgcolor:"rgba(211,47,47,.45)"}};
  const smallDangerSx={minHeight:32,px:1.5,borderRadius:"10px",textTransform:"none",fontWeight:700,fontSize:12,color:"#fff",bgcolor:"#D32F2F",boxShadow:"none","&:hover":{color:"#fff",bgcolor:"#B71C1C",boxShadow:"none"},"&.Mui-disabled":{color:"#fff",bgcolor:"rgba(211,47,47,.45)"}};
  const selectMenuProps={disableScrollLock:true,PaperProps:{elevation:0,sx:{mt:.5,bgcolor:dashboardTheme.card_background,color:dashboardTheme.text_color,border:`1px solid ${dashboardTheme.border_color}`,boxShadow:"none"}}};
  const autocompleteSx={...dashboardTheme.inputStyles,"& .MuiAutocomplete-paper":{bgcolor:dashboardTheme.card_background,color:dashboardTheme.text_color,border:`1px solid ${dashboardTheme.border_color}`,boxShadow:"none"}};
  const decimalInputProps={inputMode:"decimal",pattern:"[0-9]*\\.?[0-9]*"};
  const dialogPaperProps={elevation:0,sx:{width:"100%",maxWidth:520,borderRadius:"16px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.card_background,color:dashboardTheme.text_color,boxShadow:"none"}};

  if(empresaLoading||loading)return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Typography sx={{p:4,textAlign:"center",color:dashboardTheme.muted_text_color}}>Cargando...</Typography>
      </Box>
    </Box>
  );

  return(
    <Box sx={{minHeight:"100vh",bgcolor:dashboardTheme.dashboard_background,display:"flex",color:dashboardTheme.text_color,overflowX:"hidden"}}>
      <Sidebar open={sidebarOpen} onClose={()=>setSidebarOpen(false)}/>
      <Box sx={{flex:1,minWidth:0,minHeight:"100vh",display:"flex",flexDirection:"column"}}>
        <TopBar sidebarOpen={sidebarOpen} onToggleSidebar={()=>setSidebarOpen(prev=>!prev)}/>
        <Box component="main" sx={{flex:1,width:"100%",p:{xs:1.5,sm:2,md:3}}}>
          <Box sx={{width:{xs:"100%",lg:"95%"},mx:"auto",mt:{xs:1,md:1.5}}}>
            <Box sx={{display:"flex",alignItems:{xs:"stretch",sm:"center"},justifyContent:"space-between",flexDirection:{xs:"column",sm:"row"},gap:1.5,mb:2}}>
              <Box>
                <Typography sx={{fontSize:{xs:24,sm:26,md:30},fontWeight:600,color:dashboardTheme.primary_color,lineHeight:1.2}}>{isNew?"Nueva compra o gasto":"Detalle de compra o gasto"}</Typography>
                {!isNew&&<Typography sx={{fontSize:13,fontWeight:500,color:dashboardTheme.muted_text_color,mt:.4}}>Estado: {documentState==="ANULADO"?"Anulado":"Registrado"} · Pago: {paymentStatus(purchase?.estado_pago)}</Typography>}
              </Box>
              <Box sx={{display:"flex",gap:1,flexWrap:"wrap"}}>
                <Button variant="outlined" onClick={()=>navigate("/admin-panel/compras")} sx={outlinedButtonSx}>Regresar</Button>
                {editable&&(
                  <Button component="label" variant="outlined" disabled={readingXml||saving} sx={outlinedButtonSx}>
                    {readingXml?"Procesando XML...":"Cargar factura XML"}
                    <input hidden type="file" accept=".xml,text/xml,application/xml" onChange={e=>{const file=e.target.files?.[0];if(file)readXml(file);e.target.value="";}}/>
                  </Button>
                )}
              </Box>
            </Box>

            <Paper elevation={0} sx={{...dashboardTheme.tableStyles.wrapper,p:{xs:1.5,md:2}}}>
              <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(4,minmax(0,1fr))"},gap:1.5}}>
                <FormControl fullWidth size="small" sx={dashboardTheme.inputStyles}>
                  <InputLabel>Tipo de documento</InputLabel>
                  <Select label="Tipo de documento" value={form.tipo_documento} disabled={!editable} onChange={e=>handleForm("tipo_documento",e.target.value)} MenuProps={selectMenuProps}>
                    {(catalogs.tipos_documento.length?catalogs.tipos_documento:[{value:"FACTURA",label:"Factura"},{value:"NOTA_VENTA",label:"Nota de venta"},{value:"RECIBO",label:"Recibo"},{value:"SIN_COMPROBANTE",label:"Sin comprobante"},{value:"OTRO",label:"Otro"}]).map(item=><MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField fullWidth size="small" label="Número de documento" value={form.numero_documento} disabled={!editable} onChange={e=>handleForm("numero_documento",e.target.value)} sx={dashboardTheme.inputStyles}/>
                <TextField fullWidth size="small" type="date" label="Fecha" value={form.fecha_documento} disabled={!editable} onChange={e=>handleForm("fecha_documento",e.target.value)} InputLabelProps={{shrink:true}} sx={dashboardTheme.inputStyles}/>
                <TextField fullWidth size="small" label="Origen" value={form.origen==="XML"?"Factura XML":"Registro manual"} disabled sx={dashboardTheme.inputStyles}/>
              </Box>

              <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:1.5,mt:1.5}}>
                <Autocomplete options={catalogs.terceros} value={selectedThirdParty} disabled={!editable} getOptionLabel={option=>`${option.razon_social||option.nombre_comercial||""}${option.ruc||option.cedula?` - ${option.ruc||option.cedula}`:""}`} isOptionEqualToValue={(option,value)=>String(option.id)===String(value.id)} onChange={(_event,value)=>handleThirdParty(value)} renderInput={params=><TextField {...params} size="small" label="Proveedor registrado" sx={dashboardTheme.inputStyles}/>} noOptionsText="No se encontraron proveedores"/>
                <TextField fullWidth size="small" label="Proveedor" value={form.tercero_razon_social} disabled={!editable} onChange={e=>handleForm("tercero_razon_social",e.target.value)} sx={dashboardTheme.inputStyles}/>
                <TextField fullWidth size="small" label="Identificación" value={form.tercero_identificacion} disabled={!editable} onChange={e=>handleForm("tercero_identificacion",e.target.value)} sx={dashboardTheme.inputStyles}/>
                <TextField fullWidth size="small" label="Concepto" value={form.concepto} disabled={!editable} onChange={e=>handleForm("concepto",e.target.value)} sx={dashboardTheme.inputStyles}/>
              </Box>

              {form.origen==="XML"&&(
                <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:1.5,mt:1.5}}>
                  <TextField fullWidth size="small" label="Clave de acceso" value={form.clave_acceso} disabled={!editable} onChange={e=>handleForm("clave_acceso",e.target.value)} sx={dashboardTheme.inputStyles}/>
                  <TextField fullWidth size="small" label="Autorización" value={form.autorizacion} disabled={!editable} onChange={e=>handleForm("autorizacion",e.target.value)} sx={dashboardTheme.inputStyles}/>
                </Box>
              )}

              <TextField fullWidth size="small" label="Observación" multiline minRows={2} value={form.observacion} disabled={!editable} onChange={e=>handleForm("observacion",e.target.value)} sx={{mt:1.5,...dashboardTheme.inputStyles}}/>

              <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"center",mt:3,mb:1.3,gap:1}}>
                <Typography sx={{fontSize:18,fontWeight:700,color:dashboardTheme.text_color}}>Detalle</Typography>
                {editable&&<Button variant="outlined" onClick={addDetail} sx={outlinedButtonSx}>Añadir detalle</Button>}
              </Box>

              <TableContainer sx={{border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",overflowX:"auto"}}>
                <Table size="small" sx={{minWidth:1250}}>
                  <TableHead>
                    <TableRow sx={dashboardTheme.tableStyles.headRow}>
                      <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:135}}>Destino</TableCell>
                      <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:280}}>Producto / Categoría</TableCell>
                      <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:230}}>Descripción</TableCell>
                      <TableCell sx={dashboardTheme.tableStyles.headCell}>Cantidad</TableCell>
                      <TableCell sx={dashboardTheme.tableStyles.headCell}>Precio</TableCell>
                      <TableCell sx={dashboardTheme.tableStyles.headCell}>Descuento</TableCell>
                      <TableCell sx={dashboardTheme.tableStyles.headCell}>IVA %</TableCell>
                      <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Total</TableCell>
                      {editable&&<TableCell sx={dashboardTheme.tableStyles.headCell}/>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.map((item,index)=>{
                      const calculated=calculateDetail(item);
                      const selectedProduct=catalogs.productos.find(product=>String(product.id)===String(item.producto_id))||null;
                      const selectedCategory=catalogs.categorias.find(category=>String(category.id)===String(item.categoria_id))||null;
                      return(
                        <TableRow key={index} sx={dashboardTheme.tableStyles.bodyRow}>
                          <TableCell>
                            <FormControl fullWidth size="small" sx={dashboardTheme.inputStyles}>
                              <Select value={item.destino} disabled={!editable} onChange={e=>updateDetail(index,"destino",e.target.value)} MenuProps={selectMenuProps}>
                                <MenuItem value="GASTO">Gasto</MenuItem>
                                <MenuItem value="INVENTARIO">Inventario</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            {item.destino==="INVENTARIO"?(
                              <Autocomplete options={catalogs.productos} value={selectedProduct} disabled={!editable} getOptionLabel={option=>`${option.name||""}${option.code?` - ${option.code}`:""}${option.barcode?` - ${option.barcode}`:""}${option.codigo_auxiliar?` - ${option.codigo_auxiliar}`:""}`} isOptionEqualToValue={(option,value)=>String(option.id)===String(value.id)} filterOptions={(options,{inputValue})=>{const q=inputValue.toLowerCase().trim();if(!q)return options.slice(0,50);return options.filter(product=>String(product.name||"").toLowerCase().includes(q)||String(product.code||"").toLowerCase().includes(q)||String(product.barcode||"").toLowerCase().includes(q)||String(product.codigo_auxiliar||"").toLowerCase().includes(q)).slice(0,50);}} onChange={(_event,value)=>updateDetail(index,"producto_id",value?.id||"")} renderInput={params=><TextField {...params} size="small" placeholder="Buscar producto..." sx={autocompleteSx}/>} noOptionsText="No se encontraron productos" openOnFocus ListboxProps={{style:{maxHeight:300}}}/>
                            ):(
                              <Autocomplete options={catalogs.categorias} value={selectedCategory} disabled={!editable} getOptionLabel={option=>option.nombre_completo||option.nombre||""} isOptionEqualToValue={(option,value)=>String(option.id)===String(value.id)} filterOptions={(options,{inputValue})=>{const q=inputValue.toLowerCase().trim();if(!q)return options.slice(0,50);return options.filter(category=>String(category.nombre_completo||category.nombre||"").toLowerCase().includes(q)).slice(0,50);}} onChange={(_event,value)=>updateDetail(index,"categoria_id",value?.id||"")} renderInput={params=><TextField {...params} size="small" placeholder="Buscar categoría de gasto..." sx={autocompleteSx}/>} noOptionsText="No se encontraron categorías" openOnFocus ListboxProps={{style:{maxHeight:300}}}/>
                            )}
                          </TableCell>
                          <TableCell><TextField fullWidth size="small" value={item.descripcion} disabled={!editable} onChange={e=>updateDetail(index,"descripcion",e.target.value)} sx={dashboardTheme.inputStyles}/></TableCell>
                          <TableCell><TextField size="small" type="text" value={item.cantidad} disabled={!editable} inputProps={decimalInputProps} onChange={e=>updateDetail(index,"cantidad",e.target.value)} sx={{minWidth:95,...dashboardTheme.inputStyles}}/></TableCell>
                          <TableCell><TextField size="small" type="text" value={item.precio_unitario} disabled={!editable} inputProps={decimalInputProps} onChange={e=>updateDetail(index,"precio_unitario",e.target.value)} sx={{minWidth:105,...dashboardTheme.inputStyles}}/></TableCell>
                          <TableCell><TextField size="small" type="text" value={item.descuento} disabled={!editable} inputProps={decimalInputProps} onChange={e=>updateDetail(index,"descuento",e.target.value)} sx={{minWidth:105,...dashboardTheme.inputStyles}}/></TableCell>
                          <TableCell><TextField size="small" type="text" value={item.iva_porcentaje} disabled={!editable} inputProps={decimalInputProps} onChange={e=>updateDetail(index,"iva_porcentaje",e.target.value)} sx={{minWidth:85,...dashboardTheme.inputStyles}}/></TableCell>
                          <TableCell align="right" sx={{fontWeight:700,whiteSpace:"nowrap"}}>${formatMoney(calculated.total)}</TableCell>
                          {editable&&<TableCell><Button variant="contained" size="small" onClick={()=>removeDetail(index)} sx={smallDangerSx}>Quitar</Button></TableCell>}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{display:"flex",justifyContent:"flex-end",mt:2}}>
                <Paper elevation={0} sx={{minWidth:270,p:1.5,borderRadius:"12px",border:`1px solid ${dashboardTheme.border_color}`,bgcolor:dashboardTheme.dashboard_background}}>
                  <Box sx={{display:"flex",justifyContent:"space-between",gap:2}}>
                    <Typography sx={{fontWeight:600,color:dashboardTheme.muted_text_color}}>Total</Typography>
                    <Typography sx={{fontSize:20,fontWeight:700,color:dashboardTheme.primary_color}}>${formatMoney(purchaseTotal)}</Typography>
                  </Box>
                </Paper>
              </Box>

              {isNew&&(
                <Box sx={{mt:3}}>
                  <Typography sx={{fontSize:18,fontWeight:700,color:dashboardTheme.text_color,mb:1.5}}>Pago inicial</Typography>
                  <Box sx={{display:"flex",gap:1,flexWrap:"wrap",mb:1.5}}>
                    <Button variant="outlined" size="small" onClick={()=>setInitialFullPayment("CAJA")} sx={outlinedButtonSx}>Total en caja</Button>
                    <Button variant="outlined" size="small" onClick={()=>setInitialFullPayment("EFECTIVO")} sx={outlinedButtonSx}>Total en efectivo</Button>
                    <Button variant="outlined" size="small" onClick={()=>setInitialFullPayment("BANCO")} sx={outlinedButtonSx}>Total en banco</Button>
                  </Box>
                  <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:1.5}}>
                    <TextField fullWidth size="small" type="date" label="Fecha de pago" value={initialPayment.fecha_pago} onChange={e=>updateInitialPayment("fecha_pago",e.target.value)} InputLabelProps={{shrink:true}} sx={dashboardTheme.inputStyles}/>
                    <TextField fullWidth size="small" type="text" label="Caja" value={initialPayment.caja} inputProps={decimalInputProps} onChange={e=>updateInitialPayment("caja",e.target.value)} sx={dashboardTheme.inputStyles}/>
                    <TextField fullWidth size="small" type="text" label="Efectivo" value={initialPayment.efectivo} inputProps={decimalInputProps} onChange={e=>updateInitialPayment("efectivo",e.target.value)} sx={dashboardTheme.inputStyles}/>
                    <TextField fullWidth size="small" type="text" label="Banco" value={initialPayment.banco} inputProps={decimalInputProps} onChange={e=>updateInitialPayment("banco",e.target.value)} sx={dashboardTheme.inputStyles}/>
                    <TextField fullWidth size="small" label="Código de transferencia" value={initialPayment.codigo_transferencia} disabled={initialBanco<=0} onChange={e=>updateInitialPayment("codigo_transferencia",e.target.value)} sx={dashboardTheme.inputStyles}/>
                  </Box>
                  <TextField fullWidth size="small" label="Observación del pago" multiline minRows={2} value={initialPayment.observacion} onChange={e=>updateInitialPayment("observacion",e.target.value)} sx={{mt:1.5,...dashboardTheme.inputStyles}}/>
                  <Box sx={{display:"flex",justifyContent:"flex-end",mt:1.5}}>
                    <Typography sx={{fontWeight:700,color:dashboardTheme.text_color}}>Pago inicial: ${formatMoney(initialPaymentTotal)}</Typography>
                  </Box>
                </Box>
              )}

              {isNew&&(
                <Box sx={{display:"flex",justifyContent:"flex-end",gap:1,flexWrap:"wrap",mt:3}}>
                  <Button variant="contained" onClick={savePurchase} disabled={saving||readingXml||actionLoading} sx={dashboardTheme.buttonStyles.contained}>{saving?"Registrando...":"Registrar compra o gasto"}</Button>
                </Box>
              )}
            </Paper>

            {!isNew&&purchase&&(
              <>
                <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"repeat(3,minmax(0,1fr))"},gap:1.2,mt:2}}>
                  <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}>
                    <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Total</Typography>
                    <Typography sx={{fontSize:21,fontWeight:700,color:dashboardTheme.text_color}}>${formatMoney(purchase.total)}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}>
                    <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Pagado</Typography>
                    <Typography sx={{fontSize:21,fontWeight:700,color:dashboardTheme.text_color}}>${formatMoney(totalPaid)}</Typography>
                  </Paper>
                  <Paper elevation={0} sx={{p:1.5,border:`1px solid ${dashboardTheme.border_color}`,borderRadius:"12px",bgcolor:dashboardTheme.card_background}}>
                    <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Saldo pendiente</Typography>
                    <Typography sx={{fontSize:21,fontWeight:700,color:pendingBalance>0?"#D97706":dashboardTheme.text_color}}>${formatMoney(pendingBalance)}</Typography>
                  </Paper>
                </Box>

                {canCreate&&documentState==="REGISTRADO"&&pendingBalance>0&&(
                  <Paper elevation={0} sx={{...dashboardTheme.tableStyles.wrapper,p:{xs:1.5,md:2},mt:2}}>
                    <Typography sx={{fontSize:18,fontWeight:700,color:dashboardTheme.text_color,mb:1.5}}>Registrar pago</Typography>
                    <Box sx={{display:"flex",gap:1,flexWrap:"wrap",mb:1.5}}>
                      <Button variant="outlined" size="small" onClick={()=>setAdditionalFullPayment("CAJA")} sx={outlinedButtonSx}>Saldo en caja</Button>
                      <Button variant="outlined" size="small" onClick={()=>setAdditionalFullPayment("EFECTIVO")} sx={outlinedButtonSx}>Saldo en efectivo</Button>
                      <Button variant="outlined" size="small" onClick={()=>setAdditionalFullPayment("BANCO")} sx={outlinedButtonSx}>Saldo en banco</Button>
                    </Box>
                    <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"repeat(2,minmax(0,1fr))"},gap:1.5}}>
                      <TextField fullWidth size="small" type="date" label="Fecha de pago" value={additionalPayment.fecha_pago} onChange={e=>updateAdditionalPayment("fecha_pago",e.target.value)} InputLabelProps={{shrink:true}} sx={dashboardTheme.inputStyles}/>
                      <TextField fullWidth size="small" type="text" label="Caja" value={additionalPayment.caja} inputProps={decimalInputProps} onChange={e=>updateAdditionalPayment("caja",e.target.value)} sx={dashboardTheme.inputStyles}/>
                      <TextField fullWidth size="small" type="text" label="Efectivo" value={additionalPayment.efectivo} inputProps={decimalInputProps} onChange={e=>updateAdditionalPayment("efectivo",e.target.value)} sx={dashboardTheme.inputStyles}/>
                      <TextField fullWidth size="small" type="text" label="Banco" value={additionalPayment.banco} inputProps={decimalInputProps} onChange={e=>updateAdditionalPayment("banco",e.target.value)} sx={dashboardTheme.inputStyles}/>
                      <TextField fullWidth size="small" label="Código de transferencia" value={additionalPayment.codigo_transferencia} disabled={additionalBanco<=0} onChange={e=>updateAdditionalPayment("codigo_transferencia",e.target.value)} sx={dashboardTheme.inputStyles}/>
                    </Box>
                    <TextField fullWidth size="small" label="Observación" multiline minRows={2} value={additionalPayment.observacion} onChange={e=>updateAdditionalPayment("observacion",e.target.value)} sx={{mt:1.5,...dashboardTheme.inputStyles}}/>
                    <Box sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:2,flexWrap:"wrap",mt:2}}>
                      <Box>
                        <Typography sx={{fontSize:12.5,color:dashboardTheme.muted_text_color}}>Total de este pago</Typography>
                        <Typography sx={{fontSize:21,fontWeight:700,color:dashboardTheme.primary_color}}>${formatMoney(additionalPaymentTotal)}</Typography>
                      </Box>
                      <Button variant="contained" onClick={saveAdditionalPayment} disabled={paymentSaving} sx={dashboardTheme.buttonStyles.contained}>{paymentSaving?"Guardando...":"Registrar pago"}</Button>
                    </Box>
                  </Paper>
                )}

                <Paper elevation={0} sx={{...dashboardTheme.tableStyles.wrapper,mt:2}}>
                  <Box sx={{p:{xs:1.5,md:2},pb:1}}>
                    <Typography sx={{fontSize:18,fontWeight:700,color:dashboardTheme.text_color}}>Pagos</Typography>
                  </Box>
                  <Box sx={dashboardTheme.tableStyles.tableArea}>
                    <TableContainer sx={dashboardTheme.tableStyles.container}>
                      <Table size="small" sx={dashboardTheme.tableStyles.table}>
                        <TableHead>
                          <TableRow sx={dashboardTheme.tableStyles.headRow}>
                            <TableCell sx={dashboardTheme.tableStyles.headCell}>Fecha</TableCell>
                            <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Caja</TableCell>
                            <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Efectivo</TableCell>
                            <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Banco</TableCell>
                            <TableCell sx={{...dashboardTheme.tableStyles.headCell,minWidth:170}}>Código</TableCell>
                            <TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Total</TableCell>
                            <TableCell sx={dashboardTheme.tableStyles.headCell}>Forma</TableCell>
                            <TableCell sx={dashboardTheme.tableStyles.headCell}>Estado</TableCell>
                            {canDelete&&<TableCell align="right" sx={dashboardTheme.tableStyles.headCell}>Acción</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(purchase.pagos||[]).length?purchase.pagos.map(pago=>(
                            <TableRow key={pago.id} sx={dashboardTheme.tableStyles.bodyRow}>
                              <TableCell>{formatDate(pago.fecha_pago)}</TableCell>
                              <TableCell align="right">${formatMoney(pago.caja)}</TableCell>
                              <TableCell align="right">${formatMoney(pago.efectivo)}</TableCell>
                              <TableCell align="right">${formatMoney(pago.banco)}</TableCell>
                              <TableCell>{pago.codigo_transferencia||"-"}</TableCell>
                              <TableCell align="right" sx={{fontWeight:700}}>${formatMoney(pago.total)}</TableCell>
                              <TableCell>{pago.forma_pago==="CAJA"?"Caja":pago.forma_pago==="BANCO"?"Banco":pago.forma_pago==="MIXTO"?"Mixto":"Efectivo"}</TableCell>
                              <TableCell>{pago.estado==="ANULADO"?"Anulado":"Activo"}</TableCell>
                              {canDelete&&(
                                <TableCell align="right">
                                  {pago.estado!=="ANULADO"&&<Button variant="contained" size="small" onClick={()=>{setPaymentToAnnul(pago);setMotivoPagoAnulacion("");}} sx={smallDangerSx}>Anular</Button>}
                                </TableCell>
                              )}
                            </TableRow>
                          )):(
                            <TableRow>
                              <TableCell colSpan={canDelete?9:8} align="center" sx={{py:3,color:dashboardTheme.muted_text_color}}>No existen pagos registrados.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </Paper>

                {documentState==="REGISTRADO"&&canDelete&&(
                  <Box sx={{display:"flex",justifyContent:"flex-end",mt:2}}>
                    <Button variant="contained" onClick={()=>{setMotivoAnulacion("");setAnnulPurchaseOpen(true);}} disabled={actionLoading} sx={dangerButtonSx}>Anular compra o gasto</Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Dialog open={annulPurchaseOpen} onClose={()=>{if(!actionLoading)setAnnulPurchaseOpen(false);}} PaperProps={dialogPaperProps}>
        <DialogTitle sx={{fontWeight:700}}>Anular compra o gasto</DialogTitle>
        <DialogContent>
          <Typography sx={{fontSize:14,color:dashboardTheme.muted_text_color,mb:1.5}}>Indique el motivo de la anulación.</Typography>
          <TextField autoFocus fullWidth size="small" label="Motivo" multiline minRows={3} value={motivoAnulacion} onChange={e=>setMotivoAnulacion(e.target.value)} sx={dashboardTheme.inputStyles}/>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2.5,gap:1}}>
          <Button variant="outlined" onClick={()=>setAnnulPurchaseOpen(false)} disabled={actionLoading} sx={outlinedButtonSx}>Cancelar</Button>
          <Button variant="contained" onClick={annulPurchase} disabled={actionLoading} sx={dangerButtonSx}>{actionLoading?"Anulando...":"Anular"}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(paymentToAnnul)} onClose={()=>{if(!paymentAnnulling)setPaymentToAnnul(null);}} PaperProps={dialogPaperProps}>
        <DialogTitle sx={{fontWeight:700}}>Anular pago</DialogTitle>
        <DialogContent>
          {paymentToAnnul&&<Typography sx={{fontSize:14,color:dashboardTheme.muted_text_color,mb:1.5}}>Pago de ${formatMoney(paymentToAnnul.total)}</Typography>}
          <TextField autoFocus fullWidth size="small" label="Motivo" multiline minRows={3} value={motivoPagoAnulacion} onChange={e=>setMotivoPagoAnulacion(e.target.value)} sx={dashboardTheme.inputStyles}/>
        </DialogContent>
        <DialogActions sx={{px:3,pb:2.5,gap:1}}>
          <Button variant="outlined" onClick={()=>{setPaymentToAnnul(null);setMotivoPagoAnulacion("");}} disabled={paymentAnnulling} sx={outlinedButtonSx}>Cancelar</Button>
          <Button variant="contained" onClick={annulPayment} disabled={paymentAnnulling} sx={dangerButtonSx}>{paymentAnnulling?"Anulando...":"Anular"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}